import type * as THREE from "three";

export type SphericalParams = {
  distance: number;
  height: number;
  azimuth: number;
  polar: number;
};

/**
 * Computes spherical camera parameters (distance, height, azimuth, polar)
 * from the current camera position relative to a sight position.
 *
 * This is the inverse of SightManager.getCameraSetupForSight():
 *   x = distance * sin(polar) * cos(azimuth)
 *   y = distance * cos(polar) + height
 *   z = distance * sin(polar) * sin(azimuth)
 */
export const computeSphericalFromCamera = (
  cameraPosition: THREE.Vector3,
  sightPosition: { x: number; y: number; z: number },
  currentHeight: number = 0,
): SphericalParams => {
  const offsetX = cameraPosition.x - sightPosition.x;
  const offsetY = cameraPosition.y - sightPosition.y;
  const offsetZ = cameraPosition.z - sightPosition.z;

  const azimuth = Math.atan2(offsetZ, offsetX);
  const horizontalDist = Math.sqrt(offsetX * offsetX + offsetZ * offsetZ);
  const verticalOffset = offsetY - currentHeight;
  const polar = Math.atan2(horizontalDist, verticalOffset);
  const distance = Math.sqrt(
    horizontalDist * horizontalDist + verticalOffset * verticalOffset,
  );

  return { distance, height: currentHeight, azimuth, polar };
};
